import { PermissionGate } from "@components/PermissionGate";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Group,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconPencil, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";

import {
  TableEmptyRow,
  TableErrorRow,
  TableSkeletonRows,
} from "@components/Table";

import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { useGetPartners } from "@hooks/queries/clinic/useGetPartners";
import { useTableColumnCount } from "@hooks/useTableColumnCount";

import { Partner } from "@customTypes/partner";

import { StyledResourceManagement } from "../../ResourceManagementTabs.styled";

import { AddPartnerModal } from "./components/AddPartnerModal";
import { EditPartnerModal } from "./components/EditPartnerModal";


type PartnerCategoryFilter = "all" | "organization" | "independent";

export const PartnersTab = () => {
  const { t } = useTranslation();

  // Table column count for skeleton loading states
  const { tableRef } = useTableColumnCount();

  // Search state
  const [searchFilter, setSearchFilter] = useState("");
  const [partnerCategoryFilter, setPartnerCategoryFilter] =
    useState<PartnerCategoryFilter>("all");

  // API call for partners data
  const {
    data: partners,
    isLoading,
    isSuccess,
    isError,
  } = useGetPartners({ managementView: true });

  // Filtered partners based on category and search
  const filteredPartners = useMemo(() => {
    if (!partners) return [];

    const lowerSearch = searchFilter.trim().toLowerCase();

    return partners.filter(partner => {
      const matchesCategory =
        partnerCategoryFilter === "all" ||
        (partnerCategoryFilter === "organization" &&
          partner.is_organization === true) ||
        (partnerCategoryFilter === "independent" &&
          partner.is_organization !== true);
      const matchesSearch =
        !lowerSearch ||
        partner.name.toLowerCase().includes(lowerSearch) ||
        partner.enterprise_id?.toLowerCase().includes(lowerSearch) ||
        partner.slug?.toLowerCase().includes(lowerSearch);

      return matchesCategory && matchesSearch;
    });
  }, [partnerCategoryFilter, partners, searchFilter]);

  // Modal states
  const [addPartnerModalOpened, { open: openAddPartnerModal, close: closeAddPartnerModal }] = useDisclosure(false);
  const [editPartnerModalOpened, { open: openEditPartnerModal, close: closeEditPartnerModal }] = useDisclosure(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);

  // Partner table rows
  const partnerRows = useMemo(() => {
    return filteredPartners.map(partner => (
      <Table.Tr key={partner._id}>
        <Table.Td>
          <Group gap={12} align="center">
            <Avatar size={32} radius="md" src={partner.logo} color="blue">
              {partner.name.substring(0, 2).toUpperCase()}
            </Avatar>
            <Stack gap={0}>
              <Text fz={14} fw={500} lh={1.2}>
                {partner.name}
              </Text>
              <Text fz={12} c="dimmed">
                {partner.slug || "-"}
              </Text>
            </Stack>
          </Group>
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400}>
            {partner.enterprise_id || "-"}
          </Text>
        </Table.Td>
        <Table.Td>
          <Badge
            variant="light"
            color={partner.is_organization ? "blue" : "cyan"}>
            {partner.is_organization
              ? t("organization")
              : t("independent-partner")}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Badge 
            variant="light" 
            color="gray"
            tt="capitalize"
          >
            {(partner.practice_type || "other").replace("_", " ")}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Text fz={14} fw={400} c="dimmed">
            {partner.user_count || 0} users
          </Text>
        </Table.Td>
        <Table.Td>
          <Badge color={partner.visibility === "public" ? "green" : "gray"}>
            {partner.visibility}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Badge color={partner.is_active ? "green" : "red"} variant="outline">
            {partner.is_active ? t("active") : t("inactive")}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Group gap={8} justify="start">
            <Tooltip label={t("edit")} withArrow>
              <PermissionGate permission="partners.edit"><ActionIcon
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedPartner(partner);
                  openEditPartnerModal();
                }}>
                <IconPencil size={16} stroke={1.5} />
              </ActionIcon></PermissionGate>
            </Tooltip>
            <Tooltip label={t("delete")} withArrow>
              <PermissionGate permission="partners.delete"><ActionIcon
                variant="outline"
                color="red"
                size="sm"
                onClick={() => {
                  // TODO: Implement delete/deactivate
                }}>
                <IconTrash size={16} stroke={1.5} />
              </ActionIcon></PermissionGate>
            </Tooltip>
          </Group>
        </Table.Td>
      </Table.Tr>
    ));
  }, [filteredPartners, t]);

  return (
    <StyledResourceManagement>
      <div className="header">
        <Group w={"100%"} justify="space-between" align="center">
          <h1>{t("partners")}</h1>
        </Group>
      </div>

      <Group w={"100%"} justify="space-between" align="end" mt="md">
        <TextInput
          placeholder={t("search-for-partner", "Search for a partner...")}
          leftSection={<IconSearch size={16} />}
          value={searchFilter}
          onChange={event => setSearchFilter(event.currentTarget.value)}
          style={{ flex: 1, maxWidth: 400 }}
        />
        <Select
          label={t("partner-category")}
          value={partnerCategoryFilter}
          onChange={value =>
            setPartnerCategoryFilter(
              (value as PartnerCategoryFilter | null) ?? "all"
            )
          }
          data={[
            { value: "all", label: t("all-partners") },
            { value: "organization", label: t("organizations") },
            { value: "independent", label: t("independent-partners") },
          ]}
          allowDeselect={false}
          w={220}
        />
        <PermissionGate permission="partners.create"><Button
          leftSection={<IconPlus size={16} />}
          onClick={openAddPartnerModal}
        >
          {t("add-partner", "Add Partner")}
        </Button></PermissionGate>
      </Group>

      <ScrollArea maw={{ base: 700, md: 1024, lg1: "100%" }} mt="md">
        <Table
          ref={tableRef}
          withRowBorders={true}
          miw={1150}
          withTableBorder
          highlightOnHover
          highlightOnHoverColor="var(--clr-gray-100)">
          <Table.Thead>
            <Table.Tr bg={"var(--clr-gray-100)"}>
              <Table.Th>{t("partner")}</Table.Th>
              <Table.Th>{t("enterprise-id", "Enterprise ID")}</Table.Th>
              <Table.Th>{t("partner-category")}</Table.Th>
              <Table.Th>{t("practice-type")}</Table.Th>
              <Table.Th>{t("users")}</Table.Th>
              <Table.Th>{t("visibility")}</Table.Th>
              <Table.Th>{t("status")}</Table.Th>
              <Table.Th>{t("action")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {renderLoadingErrorData({
              isLoading,
              isError,
              isSuccess,
              dataLength: filteredPartners.length,
              errorComponent: (
                <TableErrorRow
                  colSpan={8}
                  message={t("encountered-error")}
                />
              ),
              loadingComponent: (
                <TableSkeletonRows
                  columns={8}
                  skeletonProps={{
                    widths: ["lg", "md", "md", "md", "md", "md", "md", "sm"],
                  }}
                />
              ),
              emptyDataComponent: (
                <TableEmptyRow
                  colSpan={8}
                  message={
                    searchFilter
                      ? t("empty-results-for", { searchFilter: `"${searchFilter}"` })
                      : t("no-data")
                  }
                />
              ),
              dataComponent: partnerRows,
            })}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      <PermissionGate permission="partners.create"><AddPartnerModal
        opened={addPartnerModalOpened}
        onClose={closeAddPartnerModal}
      /></PermissionGate>

      <PermissionGate permission="partners.edit"><EditPartnerModal
        opened={editPartnerModalOpened}
        onClose={closeEditPartnerModal}
        partner={selectedPartner}
        onPartnerUpdated={setSelectedPartner}
      /></PermissionGate>
    </StyledResourceManagement>
  );
};
